import { defineAdapter } from '@loop-coffee/commerce-contracts';

// Shipping through the Store API only; no carrier SDK reaches the browser. Calculated options
// (price by postal code and weight, from the backend's fulfillment provider) are priced one by
// one; a failure is an error the checkout shows with a retry, never a free option.
export function createShippingAdapter(client) {
    async function price(cartId, option) {
        if (option.price_type !== 'calculated') {
            return { id: option.id, label: option.name, amount: option.calculated_price?.calculated_amount ?? option.amount, deliveryDays: null };
        }
        const priced = await client.calculateShippingOption(option.id, cartId);
        const amount = priced?.calculated_price?.calculated_amount;
        if (typeof amount !== 'number') {
            throw new TypeError(`shipping option ${option.id} came back without a price`);
        }
        return {
            id: option.id,
            label: option.name,
            amount,
            deliveryDays: priced.calculated_price.delivery_days ?? null,
        };
    }

    return defineAdapter('shipping', {
        id: 'store-api',
        label: 'Envio',
        // Options the carrier cannot price for this address are left out; when none can be
        // priced, the first error is thrown so the checkout can tell an outage from "no option".
        async listOptions(cart) {
            const options = await client.listShippingOptions(cart.id);
            const results = await Promise.allSettled(options.map((option) => price(cart.id, option)));
            const priced = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
            const failed = results.find((r) => r.status === 'rejected');
            if (!priced.length && failed) throw failed.reason;
            return priced;
        },
        select(cart, optionId) {
            return client.addShippingMethod(cart.id, optionId);
        },
    });
}
